// src/components/salesRepDashboard/SalesRepPatientDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  IoArrowBack,
  IoPersonOutline,
  IoCalendarOutline,
  IoLocationOutline,
  IoMedkitOutline,
  IoMailOutline,
  IoCallOutline,
  IoCardOutline,
  IoChevronForward,
  IoAlertCircle,
  IoDocumentTextOutline,
  IoTimeOutline,
  IoChatbubbleOutline,
  IoPencilOutline
} from "react-icons/io5";
import { Loader2, Package, Truck } from "lucide-react";
import toast from "react-hot-toast";

/**
 * ProMed Health Plus — Sales Rep Patient Detail (Teal/Green Theme)
 * - Matches the SalesRepOrderDetail styling
 * - Professional cards + dark mode safe
 * - Clean list-style orders with strong hover states
 */

const SalesRepPatientDetail = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const [patient, setPatient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [patientNotes, setPatientNotes] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);


  const handleSavePatientNotes = async () => {
  try {
    setIsSavingNotes(true);
    const response = await fetch(
      `https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/sales-rep/patients/${patientId}/notes/`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ salesrep_patient_notes: patientNotes }),
      }
    );

    if (!response.ok) throw new Error("Failed to save notes");

    setPatient(prev => ({ ...prev, salesrep_patient_notes: patientNotes || null }));
    setIsEditingNotes(false); // ✅ close edit mode
  } catch (err) {
    console.error("Error saving notes:", err);
    alert("Failed to save notes");
  } finally {
    setIsSavingNotes(false);
  }
};

  const handleCancelNotes = () => {
    setPatientNotes(patient?.salesrep_patient_notes || ""); // revert to saved value
    setIsEditingNotes(false);
  };

  const fetchPatientDetail = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      const response = await fetch(
        `https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/sales-rep/patients/${patientId}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch patient details");

      const data = await response.json();
      setPatient(data.patient);
      setOrders(data.orders || []);
      setPatientNotes(data.patient?.salesrep_patient_notes || "");
    } catch (err) {
      console.error("Error:", err);
      setErrorMessage(err?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPatientDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const pageBg =
    "min-h-screen " +
    "bg-gradient-to-br from-white via-teal-50/40 to-emerald-50/30 " +
    "dark:from-slate-950 dark:via-slate-950 dark:to-slate-900";

  const headerBar =
    "border-b border-teal-100/70 bg-white/80 backdrop-blur " +
    "dark:border-slate-800 dark:bg-slate-950/70";

  const surface =
    "border border-slate-200 bg-white shadow-sm " +
    "dark:border-slate-800 dark:bg-slate-900/60 dark:shadow-none";

  const surfaceSoft =
    "border border-slate-200 bg-slate-50 " +
    "dark:border-slate-800 dark:bg-slate-900/40";

  const formatDate = (value, mode = "date") => {
    if (!value) return "N/A";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "N/A";

    if (mode === "long") {
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }

    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusUi = (statusDisplay, statusRaw) => {
    const s = String(statusRaw || statusDisplay || "").toLowerCase();

    if (s.includes("delivered")) {
      return {
        label: statusDisplay || "Delivered",
        pill:
          "bg-emerald-50 text-emerald-700 ring-emerald-200 " +
          "dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30",
        dot: "bg-emerald-500",
      };
    }
    if (s.includes("cancel")) {
      return {
        label: statusDisplay || "Cancelled",
        pill:
          "bg-rose-50 text-rose-700 ring-rose-200 " +
          "dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30",
        dot: "bg-rose-500",
      };
    }
    if (s.includes("ship")) {
      return {
        label: statusDisplay || "Shipped",
        pill:
          "bg-indigo-50 text-indigo-700 ring-indigo-200 " +
          "dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/30",
        dot: "bg-indigo-500",
      };
    }
    if (s.includes("process")) {
      return {
        label: statusDisplay || "Processing",
        pill:
          "bg-sky-50 text-sky-700 ring-sky-200 " +
          "dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/30",
        dot: "bg-sky-500",
      };
    }
    return {
      label: statusDisplay || "Pending",
      pill:
        "bg-amber-50 text-amber-700 ring-amber-200 " +
        "dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30",
      dot: "bg-amber-500",
    };
  };

  const ordersCount = orders?.length || 0;

  const woundSummary = useMemo(() => {
    if (!patient) return null;
    const hasWound =
      patient.wound_type ||
      patient.wound_location ||
      patient.wound_size_length ||
      patient.wound_size_width ||
      patient.wound_drainage;
    if (!hasWound) return null;

    return {
      type: patient.wound_type || "N/A",
      location: patient.wound_location || "N/A",
      size:
        patient.wound_size_length && patient.wound_size_width
          ? `${patient.wound_size_length} × ${patient.wound_size_width} cm`
          : "N/A",
      drainage: patient.wound_drainage || "N/A",
    };
  }, [patient]);

  if (isLoading) {
    return (
      <div className={pageBg}>
        <div className="mx-auto flex min-h-screen max-w-5xl items-center justify-center px-6">
          <div className={`rounded-2xl p-8 ${surface}`}>
            <div className="flex items-center gap-4">
              <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Loading patient details
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Fetching the latest information…
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className={pageBg}>
        <div className="mx-auto flex min-h-screen max-w-5xl items-center justify-center px-6">
          <div className="w-full max-w-md rounded-2xl border border-rose-200 bg-white p-6 shadow-sm dark:border-rose-500/30 dark:bg-slate-900/70">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-rose-50 ring-1 ring-rose-200 dark:bg-rose-500/10 dark:ring-rose-500/30">
                <IoAlertCircle className="text-xl text-rose-600 dark:text-rose-300" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                  Unable to load patient
                </p>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  {errorMessage}
                </p>
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex flex-1 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
              >
                Go Back
              </button>
              <button
                onClick={fetchPatientDetail}
                className="inline-flex flex-1 items-center justify-center rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-2.5 text-sm font-extrabold text-white shadow-sm transition hover:from-teal-700 hover:to-emerald-700 hover:shadow-md"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!patient) return null;

  return (
    <div className={pageBg}>
      {/* Top Header */}
      <div className={headerBar}>
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-12">
          <button
            onClick={() => navigate(-1)}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
          >
            <IoArrowBack className="text-lg transition group-hover:-translate-x-0.5" />
            Back
          </button>

          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-teal-50 ring-1 ring-teal-100 dark:bg-teal-500/10 dark:ring-teal-500/20">
                <IoPersonOutline className="text-2xl text-teal-700 dark:text-teal-300" />
              </div>
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl">
                  {patient.full_name}
                </h1>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  Patient ID: <span className="font-semibold">{patient.id}</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
                <Package className="h-4 w-4 text-teal-600 dark:text-teal-300" />
                Orders: <span className="font-extrabold">{ordersCount}</span>
              </span>
            </div>
          </div>

          {/* Quick meta row */}
          {/* <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <MetaPill
              surface={surface}
              icon={<IoCalendarOutline className="text-teal-700 dark:text-teal-300" />}
              label="Date of Birth"
              value={patient.date_of_birth || "N/A"}
            />
            <MetaPill
              surface={surface}
              icon={<IoCallOutline className="text-teal-700 dark:text-teal-300" />}
              label="Phone"
              value={patient.phone_number || "N/A"}
            />
            <MetaPill
              surface={surface}
              icon={<IoMailOutline className="text-teal-700 dark:text-teal-300" />}
              label="Email"
              value={patient.email || "N/A"}
            />
          </div> */}
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* LEFT */}
          <div className="space-y-6 lg:col-span-8">
            {/* Patient Information */}
            <SectionCard
              surface={surface}
              title="Patient Information"
              subtitle="Contact + administrative details"
              icon={<IoPersonOutline className="text-teal-700 dark:text-teal-300" />}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {/* <InfoBlock surfaceSoft={surfaceSoft} icon={<IoCalendarOutline />} label="Date of Birth" value={patient.date_of_birth || "N/A"} />
                <InfoBlock surfaceSoft={surfaceSoft} icon={<IoCallOutline />} label="Phone" value={patient.phone_number || "N/A"} />
                <InfoBlock surfaceSoft={surfaceSoft} icon={<IoMailOutline />} label="Email" value={patient.email || "N/A"} />

                <div className="sm:col-span-2 lg:col-span-3">
                  <WideInfoBlock
                    surfaceSoft={surfaceSoft}
                    icon={<IoLocationOutline />}
                    label="Address"
                    value={
                      patient.address
                        ? `${patient.address}${patient.city ? `, ${patient.city}` : ""}${patient.state ? `, ${patient.state}` : ""}${patient.zip_code ? ` ${patient.zip_code}` : ""}`
                        : "N/A"
                    }
                  />
                </div> */}

                <InfoBlock
                  surfaceSoft={surfaceSoft}
                  icon={<IoCardOutline />}
                  label="Primary Insurance"
                  value={patient.primary_insurance || "N/A"}
                />
                <InfoBlock
                  surfaceSoft={surfaceSoft}
                  icon={<IoMedkitOutline />}
                  label="Primary Insurance #"
                  value={patient.primary_insurance_number || "N/A"}
                />
              </div>
            </SectionCard>

            {/* Wound Information */}
            {woundSummary ? (
              <SectionCard
                surface={surface}
                title="Wound Information"
                subtitle="Clinical summary"
                icon={<IoMedkitOutline className="text-teal-700 dark:text-teal-300" />}
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <InfoBlock surfaceSoft={surfaceSoft} icon={<IoMedkitOutline />} label="Type" value={woundSummary.type} />
                  <InfoBlock surfaceSoft={surfaceSoft} icon={<IoLocationOutline />} label="Location" value={woundSummary.location} />
                  <InfoBlock surfaceSoft={surfaceSoft} icon={<IoDocumentTextOutline />} label="Size" value={woundSummary.size} />
                  <InfoBlock
                    surfaceSoft={surfaceSoft}
                    icon={<IoTimeOutline />}
                    label="Drainage"
                    value={
                      woundSummary.drainage
                        ? String(woundSummary.drainage).toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
                        : "N/A"
                    }
                  />
                </div>
              </SectionCard>
            ) : null}

            {/* Orders */}
            <SectionCard
              surface={surface}
              title={`Orders (${ordersCount})`}
              subtitle="Click an order to view full details"
              icon={<Package className="h-5 w-5 text-teal-700 dark:text-teal-300" />}
              right={
                <button
                  onClick={fetchPatientDetail}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
                >
                  Refresh
                </button>
              }
            >
              {ordersCount === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center dark:border-slate-800 dark:bg-slate-900/30">
                  <Package className="mx-auto h-12 w-12 text-slate-400" />
                  <p className="mt-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    No orders yet
                  </p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Orders will appear here once placed.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.map((order) => {
                    const st = getStatusUi(order.status_display, order.status);
                    return (
                      <button
                        key={order.id}
                        onClick={() => navigate(`/sales-rep/orders/${order.id}`)}
                        className={`group w-full rounded-2xl p-5 text-left transition-all hover:shadow-md ${surfaceSoft} hover:border-teal-300 dark:hover:border-teal-700`}
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <p className="text-base font-extrabold text-slate-900 dark:text-slate-100">
                              Order #{order.order_number}
                            </p>
                            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                              {formatDate(order.created_at, "long")}
                            </p>
                          </div>

                          <div className="flex items-center gap-3">
                            <span className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-extrabold ring-1 ${st.pill}`}>
                              <span className={`h-2 w-2 rounded-full ${st.dot}`} />
                              {st.label}
                            </span>

                            <IoChevronForward className="text-xl text-slate-400 transition-all group-hover:translate-x-0.5 group-hover:text-teal-600 dark:group-hover:text-teal-300" />
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                          <MiniMetric
                            label="Care Type"
                            value={order.care_type_display || "N/A"}
                          />
                          <MiniMetric
                            label="Duration"
                            value={order.kit_duration_display || "N/A"}
                          />
                          <MiniMetric
                            label="Facility"
                            value={order.facility_name || "N/A"}
                          />
                          <MiniMetric
                            label="Tracking"
                            value={order.tracking_number || "Pending"}
                            icon={
                              order.tracking_number ? (
                                <Truck className="h-4 w-4 text-teal-600 dark:text-teal-300" />
                              ) : null
                            }
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </SectionCard>
          </div>

          {/* RIGHT */}
          <div className="space-y-6 lg:col-span-4">
            <div className={`rounded-2xl p-5 ${surface}`}>
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-teal-50 ring-1 ring-teal-100 dark:bg-teal-500/10 dark:ring-teal-500/20">
                  <IoPersonOutline className="text-lg text-teal-700 dark:text-teal-300" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                    Patient Summary
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Quick view for sales reps
                  </p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3">
                <SummaryTile surfaceSoft={surfaceSoft} label="Patient" value={patient.full_name || "N/A"} />
                <SummaryTile surfaceSoft={surfaceSoft} label="Patient ID" value={patient.id ? `#${patient.id}` : "N/A"} />
                <SummaryTile surfaceSoft={surfaceSoft} label="Orders" value={String(ordersCount)} />
              </div>

              <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
                Tip: Click any order to open the order detail page.
              </p>
            </div>

            {/* Sales Rep Notes */}
            <div className={`rounded-2xl p-5 ${surface}`}>
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-teal-50 ring-1 ring-teal-100 dark:bg-teal-500/10 dark:ring-teal-500/20">
                    <IoChatbubbleOutline className="text-lg text-teal-700 dark:text-teal-300" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                      Sales Rep Notes
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Internal — not visible to provider
                    </p>
                  </div>
                </div>

                {/* Edit button — only shown in read mode */}
                {!isEditingNotes && (
                  <button
                    onClick={() => setIsEditingNotes(true)}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-teal-300 hover:text-teal-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-teal-600 dark:hover:text-teal-300"
                  >
                    <IoPencilOutline className="text-sm" />
                    Edit
                  </button>
                )}
              </div>

              {isEditingNotes ? (
                /* Edit mode */
                <>
                  <textarea
                    value={patientNotes}
                    onChange={(e) => setPatientNotes(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSavePatientNotes();
                      }
                      if (e.key === "Escape") {
                        handleCancelNotes();
                      }
                    }}
                    placeholder="Add notes about this patient... (Enter to save, Shift+Enter for new line)"
                    rows={5}
                    autoFocus
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100 dark:placeholder-slate-500"
                  />
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={handleSavePatientNotes}
                      disabled={isSavingNotes}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-2.5 text-sm font-extrabold text-white shadow-sm transition hover:from-teal-700 hover:to-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSavingNotes ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save"
                      )}
                    </button>
                    <button
                      onClick={handleCancelNotes}
                      disabled={isSavingNotes}
                      className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:shadow-md disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                    >
                      Cancel
                    </button>
                  </div>
                  <p className="mt-2 text-center text-xs text-slate-400 dark:text-slate-500">
                    <kbd className="rounded border border-slate-200 bg-slate-100 px-1 py-0.5 text-xs dark:border-slate-700 dark:bg-slate-800">Esc</kbd> to cancel
                    {" · "}
                    <kbd className="rounded border border-slate-200 bg-slate-100 px-1 py-0.5 text-xs dark:border-slate-700 dark:bg-slate-800">Enter</kbd> to save
                  </p>
                </>
              ) : (
                /* Read mode */
                <div className={`rounded-xl p-4 ${surfaceSoft} min-h-[80px]`}>
                  {patient?.salesrep_patient_notes ? (
                    <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
                      {patient.salesrep_patient_notes}
                    </p>
                  ) : (
                    <p className="text-sm italic text-slate-400 dark:text-slate-500">
                      No notes yet. Click Edit to add one.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* <div className={`rounded-2xl p-5 ${surface}`}>
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-teal-50 ring-1 ring-teal-100 dark:bg-teal-500/10 dark:ring-teal-500/20">
                  <IoLocationOutline className="text-lg text-teal-700 dark:text-teal-300" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                    Address
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Delivery & patient location
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/40">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Full Address
                </p>
                <p className="mt-1 text-sm font-bold text-slate-900 dark:text-slate-100">
                  {patient.address
                    ? `${patient.address}${patient.city ? `, ${patient.city}` : ""}${patient.state ? `, ${patient.state}` : ""}${patient.zip_code ? ` ${patient.zip_code}` : ""}`
                    : "N/A"}
                </p>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

/** ---------- UI Blocks ---------- */

const SectionCard = ({ surface, title, subtitle, icon, right, children }) => {
  return (
    <div className={`rounded-2xl p-6 ${surface}`}>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-teal-50 ring-1 ring-teal-100 dark:bg-teal-500/10 dark:ring-teal-500/20">
            <span className="text-lg">{icon}</span>
          </div>
          <div>
            <h2 className="text-lg font-extrabold text-slate-900 dark:text-slate-100">
              {title}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          </div>
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      {children}
    </div>
  );
};

const InfoBlock = ({ surfaceSoft, icon, label, value }) => {
  return (
    <div className={`rounded-xl p-4 ${surfaceSoft}`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-teal-100 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-500/20">
          <span className="text-lg">{icon}</span>
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {label}
          </p>
          <p className="mt-1 truncate text-sm font-extrabold text-slate-900 dark:text-slate-100">
            {value ?? "N/A"}
          </p>
        </div>
      </div>
    </div>
  );
};

const WideInfoBlock = ({ surfaceSoft, icon, label, value }) => {
  return (
    <div className={`rounded-xl p-4 ${surfaceSoft}`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-teal-100 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-500/20">
          <span className="text-lg">{icon}</span>
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {label}
          </p>
          <p className="mt-1 text-sm font-extrabold text-slate-900 dark:text-slate-100 break-words">
            {value || "N/A"}
          </p>
        </div>
      </div>
    </div>
  );
};

const MetaPill = ({ surface, icon, label, value }) => {
  return (
    <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${surface}`}>
      <div className="grid h-9 w-9 place-items-center rounded-xl bg-teal-50 ring-1 ring-teal-100 dark:bg-teal-500/10 dark:ring-teal-500/20">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {label}
        </p>
        <p className="truncate text-sm font-extrabold text-slate-900 dark:text-slate-100">
          {value}
        </p>
      </div>
    </div>
  );
};

const MiniMetric = ({ label, value, icon }) => {
  return (
    <div className="rounded-xl border border-slate-200 bg-white/60 p-3 dark:border-slate-800 dark:bg-slate-950/40">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {label}
        </p>
        {icon ? <span className="shrink-0">{icon}</span> : null}
      </div>
      <p className="mt-1 truncate text-sm font-extrabold text-slate-900 dark:text-slate-100">
        {value}
      </p>
    </div>
  );
};

const SummaryTile = ({ surfaceSoft, label, value }) => {
  return (
    <div className={`rounded-xl p-3 ${surfaceSoft}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="mt-1 text-sm font-extrabold text-slate-900 dark:text-slate-100">
        {value}
      </p>
    </div>
  );
};

export default SalesRepPatientDetail;
